import { match } from "ts-pattern";
import {
  ControlDataTypeKey,
  Types,
  Schema
} from "../prop-controllers";
import { z } from "zod";
import { colorDataSchema } from "../data";
import { textStyleDataSchema } from "../text-style";
import { copyLinkData, linkDataSchema } from "../link";
import { copyResponsiveColorData } from "../responsive-color";
const buttonVariantSchema = z.enum([
  "flat",
  "outline",
  "shadow",
  "clear",
  "blocky",
  "bubbly",
  "skewed"
]);
const buttonShapeSchema = z.enum(["pill", "rounded", "square"]);
const buttonSizeSchema = z.enum(["small", "medium", "large"]);
const navigationButtonSchema = z.object({
  id: z.string(),
  type: z.literal("button"),
  payload: z.object({
    label: z.string(),
    link: linkDataSchema.optional(),
    variant: Schema.responsiveValue(buttonVariantSchema).optional(),
    shape: Schema.responsiveValue(buttonShapeSchema).optional(),
    size: Schema.responsiveValue(buttonSizeSchema).optional(),
    textColor: Schema.responsiveValue(colorDataSchema).optional(),
    color: Schema.responsiveValue(colorDataSchema).optional(),
    textStyle: Schema.responsiveValue(textStyleDataSchema).optional()
  })
});
const navigationDropdownCaretTypeSchema = z.union([
  z.literal("caret"),
  z.literal("plus"),
  z.literal("arrow-down"),
  z.literal("chevron-down")
]);
const navigationDropdownLinkSchema = z.object({
  id: z.string(),
  payload: z.object({
    label: z.string(),
    link: linkDataSchema.optional(),
    color: Schema.responsiveValue(colorDataSchema).optional(),
    textStyle: Schema.responsiveValue(textStyleDataSchema).optional()
  })
});
const navigationDropdownSchema = z.object({
  id: z.string(),
  type: z.literal("dropdown"),
  payload: z.object({
    label: z.string(),
    caret: navigationDropdownCaretTypeSchema.optional(),
    links: z.array(navigationDropdownLinkSchema).optional(),
    variant: Schema.responsiveValue(buttonVariantSchema).optional(),
    shape: Schema.responsiveValue(buttonShapeSchema).optional(),
    size: Schema.responsiveValue(buttonSizeSchema).optional(),
    textColor: Schema.responsiveValue(colorDataSchema).optional(),
    color: Schema.responsiveValue(colorDataSchema).optional(),
    textStyle: Schema.responsiveValue(textStyleDataSchema).optional()
  })
});
const navigationLinksDataSchema = z.array(
  z.union([navigationButtonSchema, navigationDropdownSchema])
);
const navigationLinksPropControllerDataV0Schema = navigationLinksDataSchema;
const NavigationLinksPropControllerDataV1Type = "prop-controllers::navigation-links::v1";
const navigationLinksPropControllerDataV1Schema = z.object({
  [ControlDataTypeKey]: z.literal(NavigationLinksPropControllerDataV1Type),
  value: navigationLinksDataSchema
});
const navigationLinksPropControllerDataSchema = z.union([
  navigationLinksPropControllerDataV0Schema,
  navigationLinksPropControllerDataV1Schema
]);
function NavigationLinks(options = {}) {
  return { type: Types.NavigationLinks, version: 1, options };
}
function getNavigationLinksPropControllerDataNavigationLinksData(data) {
  return match(data).with(
    { [ControlDataTypeKey]: NavigationLinksPropControllerDataV1Type },
    (v1) => v1.value
  ).otherwise((v0) => v0);
}
function createNavigationLinksPropControllerDataFromNavigationLinksData(navigationLinksData, definition) {
  return match(definition).with(
    { version: 1 },
    () => ({
      [ControlDataTypeKey]: NavigationLinksPropControllerDataV1Type,
      value: navigationLinksData
    })
  ).otherwise(() => navigationLinksData);
}
function getNavigationLinksPropControllerPageIds(data) {
  if (data == null)
    return [];
  const value = getNavigationLinksPropControllerDataNavigationLinksData(data);
  return value?.flatMap((item) => {
    switch (item.type) {
      case "button": {
        if (item.payload.link == null)
          return [];
        switch (item.payload.link.type) {
          case "OPEN_PAGE":
            return item.payload.link.payload.pageId == null ? [] : [item.payload.link.payload.pageId];
          default:
            return [];
        }
      }
      case "dropdown": {
        return item.payload.links?.flatMap((link) => {
          if (link.payload.link == null)
            return [];
          switch (link.payload.link.type) {
            case "OPEN_PAGE":
              return link.payload.link.payload.pageId == null ? [] : [link.payload.link.payload.pageId];
            default:
              return [];
          }
        }) ?? [];
      }
    }
  }) ?? [];
}
function getNavigationLinksPropControllerSwatchIds(data) {
  if (data == null)
    return [];
  const value = getNavigationLinksPropControllerDataNavigationLinksData(data);
  return value?.flatMap((item) => {
    switch (item.type) {
      case "button":
      case "dropdown":
        return [
          ...item.payload.color?.map((override) => override.value).map((color) => color.swatchId) ?? [],
          ...item.payload.textColor?.map((override) => override.value).map((color) => color.swatchId) ?? []
        ];
    }
  }) ?? [];
}
function copyNavigationLinksData(data, context) {
  return data?.map((item) => {
    switch (item.type) {
      case "button": {
        const { color, link } = item.payload;
        return {
          ...item,
          payload: {
            ...item.payload,
            link: copyLinkData(link, context),
            color: color != null ? copyResponsiveColorData(color, context) : void 0
          }
        };
      }
      case "dropdown": {
        const { color, links } = item.payload;
        return {
          ...item,
          payload: {
            ...item.payload,
            links: links != null ? links.map((link) => ({
              ...link,
              payload: {
                ...link.payload,
                link: copyLinkData(link.payload.link, context)
              }
            })) : void 0,
            color: color != null ? copyResponsiveColorData(color, context) : void 0
          }
        };
      }
      default:
        return item;
    }
  });
}
function copyNavigationLinksPropControllerData(data, context) {
  return match(data).with(void 0, () => void 0).with(
    { [ControlDataTypeKey]: NavigationLinksPropControllerDataV1Type },
    (v1) => ({
      [ControlDataTypeKey]: NavigationLinksPropControllerDataV1Type,
      value: copyNavigationLinksData(v1.value, context)
    })
  ).otherwise((v0) => copyNavigationLinksData(v0, context));
}
export {
  NavigationLinks,
  NavigationLinksPropControllerDataV1Type,
  copyNavigationLinksPropControllerData,
  createNavigationLinksPropControllerDataFromNavigationLinksData,
  getNavigationLinksPropControllerDataNavigationLinksData,
  getNavigationLinksPropControllerPageIds,
  getNavigationLinksPropControllerSwatchIds,
  navigationLinksPropControllerDataSchema
};
//# sourceMappingURL=navigation-links.js.map